// /admin/cookbook-journeys: everyone who started a printed cookbook in a period, how far
// they got through the wizard, and the funnel from "started" to "shipped".
// Period, stage, search and page live in the address bar (back/forward and links work).
import { Link, useNavigate } from 'react-router-dom'

import { getCookbookJourneys } from '../../../api/adminV2'
import { Chips, ListSearch, Pager, PAGE_SIZE, useListParams } from '../list'
import { formatDate, formatNumber, LoadError, Loading, Pill, useLoad } from '../ui'

export const JOURNEY_PERIODS = [
  ['today', 'Today'],
  ['week', 'This week'],
  ['30d', 'Last 30 days'],
  ['all', 'All time'],
]

const PERIOD_KEY = 'morsel_admin_journey_period'
const savedPeriod = () => {
  try {
    const p = window.localStorage.getItem(PERIOD_KEY)
    return JOURNEY_PERIODS.some(([key]) => key === p) ? p : '30d'
  } catch (e) {
    return '30d'
  }
}

const PERIOD_WORDS = {
  today: 'today',
  week: 'this week',
  '30d': 'in the last 30 days',
  all: 'since printed cookbooks began',
}

// "Got as far as" chips: the furthest stage each cookbook reached
const STAGE_CHIPS = [
  ['all', 'Everyone'],
  ['started', 'Picked a group'],
  ['recipes', 'Picked some recipes'],
  ['min_recipes', '12+ recipes'],
  ['details', 'Cover and details'],
  ['preview', 'Preview built'],
  ['shipping', 'Checked the price'],
  ['checkout', 'Payment page or request'],
  ['paid', 'Paid'],
  ['printer', 'At the printer'],
  ['shipped', 'Shipped'],
]

export const WIZARD_STEPS = ['Group', 'Recipes', 'Cover & details', 'Preview', 'Ship & pay']

const percent = (n, of) => (of ? `${Math.round((n / of) * 100)}%` : '0%')

export const AccountMarks = ({ marks }) =>
  (marks || []).map((m) => (
    <Pill key={m.key} kind={m.key === 'closed' ? 'bad' : 'warn'}>
      {m.label}
    </Pill>
  ))

// Five dots for the five wizard steps, filled up to the step they reached
export const StepDots = ({ step }) => (
  <span className="a-steps" aria-label={`Step ${step} of 5: ${WIZARD_STEPS[step - 1]}`}>
    {WIZARD_STEPS.map((name, i) => (
      <span key={name} className={`a-step-dot${i < step ? ' a-on' : ''}`} title={name} />
    ))}
    <span className="a-step-text">
      Step {step} of 5 · {WIZARD_STEPS[step - 1]}
    </span>
  </span>
)

const Funnel = ({ funnel, onPick }) => {
  const started = funnel?.[0]?.drafts || 0
  return (
    <ol className="a-funnel">
      {(funnel || []).map((s, i) => {
        const prev = i ? funnel[i - 1].drafts : s.drafts
        return (
          <li key={s.key}>
            <button type="button" className="a-funnel-row" onClick={() => onPick(s.key)}>
              <span className="a-funnel-label">{s.label}</span>
              <span className="a-funnel-track" aria-hidden="true">
                <span
                  className="a-funnel-bar"
                  style={{
                    width: started
                      ? `${Math.max((s.drafts / started) * 100, s.drafts ? 2 : 0)}%`
                      : 0,
                  }}
                />
              </span>
              <span className="a-funnel-num">
                <b>{formatNumber(s.drafts)}</b>{' '}
                <span className="a-muted">
                  {s.drafts === 1 ? 'book' : 'books'} · {formatNumber(s.people)}{' '}
                  {s.people === 1 ? 'person' : 'people'}
                  {i ? ` · ${percent(s.drafts, started)} of started` : ''}
                  {i && prev && s.drafts < prev
                    ? `, ${formatNumber(prev - s.drafts)} stopped before this`
                    : ''}
                </span>
              </span>
            </button>
          </li>
        )
      })}
    </ol>
  )
}

const CookbookJourneys = () => {
  const navigate = useNavigate()
  const { read, page, update, key } = useListParams({
    period: savedPeriod(),
    stage: 'all',
    tests: 'show',
  })
  const period = JOURNEY_PERIODS.some(([k]) => k === read('period')) ? read('period') : '30d'
  const stage = read('stage') || 'all'
  const tests = read('tests') === 'hide' ? 'hide' : 'show'
  const q = read('q')

  const { data, error, loading, reload } = useLoad(
    () => getCookbookJourneys({ period, stage, tests, q: q || undefined, page, size: PAGE_SIZE }),
    [key]
  )

  const choosePeriod = (value) => {
    try {
      window.localStorage.setItem(PERIOD_KEY, value)
    } catch (e) {
      // Private windows may refuse; the address bar still has it
    }
    update({ period: value })
  }

  const rows = data?.data || []

  return (
    <>
      <div className="a-head">
        <div>
          <h1>Cookbook journeys</h1>
          <p className="a-sub">
            Everyone who started a printed cookbook {PERIOD_WORDS[period]}, and how far they got.
            Click a row to see each step with its time.
          </p>
        </div>
        <div className="a-seg" role="group" aria-label="Cookbooks started">
          {JOURNEY_PERIODS.map(([k, label]) => (
            <button
              type="button"
              key={k}
              aria-pressed={period === k}
              onClick={() => choosePeriod(k)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {error && !data ? (
        <LoadError error={error} onRetry={reload} />
      ) : !data ? (
        <Loading />
      ) : (
        <>
          <section className={`a-panel${loading ? ' a-busy' : ''}`}>
            <div className="a-panel-head">
              <h2>
                From started to shipped: {formatNumber(data.counts?.all)}{' '}
                {data.counts?.all === 1 ? 'cookbook' : 'cookbooks'} by {formatNumber(data.people)}{' '}
                {data.people === 1 ? 'person' : 'people'}
              </h2>
            </div>
            {data.counts?.all ? (
              <Funnel funnel={data.funnel} onPick={(s) => update({ stage: s })} />
            ) : (
              <p className="a-empty">Nobody started a cookbook {PERIOD_WORDS[period]}.</p>
            )}
            <p className="a-muted a-small-text" style={{ marginTop: 12 }}>
              Each step counts every cookbook that got at least that far.{' '}
              {data.recorded_since
                ? `"Checked the price" and the times of each step are recorded since ${formatDate(
                    data.recorded_since
                  )}; older cookbooks only show it when they went further.`
                : '"Checked the price" and the times of each step are recorded from this update on; older cookbooks only show it when they went further.'}
              {data.test_accounts
                ? ` ${formatNumber(data.test_accounts)} ${
                    data.test_accounts === 1 ? 'is a test address' : 'are test addresses'
                  }${data.tests_hidden ? ', left out here' : ', counted here and marked below'}.`
                : ''}
              {data.old_print_orders
                ? ` ${formatNumber(
                    data.old_print_orders
                  )} older orders from the previous print service are not shown.`
                : ''}
            </p>
          </section>

          <section className="a-panel">
            <p className="a-muted a-small-text" style={{ margin: '0 0 6px' }}>
              Show the cookbooks that stopped at:
            </p>
            <Chips
              label="Got as far as"
              options={STAGE_CHIPS}
              value={stage}
              counts={data.counts || {}}
              onChange={(value) => update({ stage: value })}
            />
            <div className="a-toolbar">
              <ListSearch
                value={q}
                placeholder="Search name, email, family group or book title"
                onChange={(value) => update({ q: value }, { replace: !!q })}
              />
              <button
                type="button"
                className="a-chip"
                aria-pressed={tests === 'hide'}
                onClick={() => update({ tests: tests === 'hide' ? 'show' : 'hide' })}
              >
                Leave out test addresses
              </button>
              {loading ? <span className="a-muted a-small-text">Loading…</span> : null}
            </div>
            {error ? <LoadError error={error} onRetry={reload} /> : null}
            <div className="a-table-wrap">
              <table className="a-table">
                <thead>
                  <tr>
                    <th>Person</th>
                    <th>Plan</th>
                    <th>Family group</th>
                    <th>Started</th>
                    <th>Last activity</th>
                    <th>How far</th>
                    <th className="a-num-cell">Recipes</th>
                    <th>Now</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length ? (
                    rows.map((j) => (
                      <tr
                        className="a-click"
                        key={j.id}
                        onClick={() => navigate(`/admin/cookbook-journeys/${j.id}`)}
                      >
                        <td className="a-who">
                          <b>
                            {j.person?.id ? (
                              <Link
                                to={`/admin/people/${j.person.id}`}
                                onClick={(e) => e.stopPropagation()}
                              >
                                {j.person.fullname || '(no name)'}
                              </Link>
                            ) : (
                              '(account gone)'
                            )}
                          </b>
                          <span className="a-line">{j.person?.email}</span>
                          <span className="a-line">
                            Signed up {formatDate(j.person?.signed_up_at)}{' '}
                            <AccountMarks marks={j.person?.marks} />
                          </span>
                        </td>
                        <td>{j.person?.plan_label || 'None'}</td>
                        <td>
                          {j.group ? (
                            <Link
                              to={`/admin/groups/${j.group.id}`}
                              onClick={(e) => e.stopPropagation()}
                            >
                              {j.group.name}
                            </Link>
                          ) : (
                            <span className="a-muted">none</span>
                          )}
                        </td>
                        <td className="a-nowrap">{formatDate(j.started_at)}</td>
                        <td className="a-nowrap">{formatDate(j.last_activity_at)}</td>
                        <td>
                          <StepDots step={j.wizard_step} />
                        </td>
                        <td className="a-num-cell a-nowrap">
                          <span className={j.recipe_count < j.min_recipes ? 'a-short' : ''}>
                            {j.recipe_count}
                          </span>
                          <span className="a-muted"> / {j.min_recipes}</span>
                        </td>
                        <td>
                          <Pill kind={j.status?.tone}>{j.status?.label}</Pill>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="8" className="a-muted">
                        {q ? `Nobody matches "${q}" here.` : 'No cookbooks here.'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <Pager
              data={data}
              page={page}
              note="newest started first"
              onPage={(p) => update({ page: p })}
            />
          </section>
        </>
      )}
    </>
  )
}

export default CookbookJourneys
